import { Slot } from '@radix-ui/react-slot';
import { ChevronRight, MoreHorizontal } from 'lucide-react';
import * as React from 'react';

import { cn } from '@lib/utils';

type BreadcrumbProps = React.ComponentProps<'nav'> & {
  separator?: React.ReactNode;
};

const Breadcrumb = (props: BreadcrumbProps) => {
  return (
    <nav
      aria-label="breadcrumb"
      {...props}
    />
  );
};
Breadcrumb.displayName = 'Breadcrumb';

const BreadcrumbList = (props: React.ComponentProps<'ol'>) => {
  const { className, ...otherProps } = props;

  return (
    <ol
      className={cn(
        'flex flex-wrap items-center gap-1.5 wrap-break-word text-sm text-muted-foreground sm:gap-2.5',
        className,
      )}
      {...otherProps}
    />
  );
};
BreadcrumbList.displayName = 'BreadcrumbList';

const BreadcrumbItem = (props: React.ComponentProps<'li'>) => {
  const { className, ...otherProps } = props;

  return (
    <li
      className={cn('inline-flex items-center gap-1.5', className)}
      {...otherProps}
    />
  );
};
BreadcrumbItem.displayName = 'BreadcrumbItem';

type BreadcrumbLinkProps = React.ComponentProps<'a'> & {
  asChild?: boolean;
};

const BreadcrumbLink = (props: BreadcrumbLinkProps) => {
  const { asChild, className, ...otherProps } = props;
  const Comp = asChild ? Slot : 'a';

  return (
    <Comp
      className={cn('transition-colors hover:text-foreground', className)}
      {...otherProps}
    />
  );
};
BreadcrumbLink.displayName = 'BreadcrumbLink';

const BreadcrumbPage = (props: React.ComponentProps<'span'>) => {
  const { className, ...otherProps } = props;

  return (
    <span
      aria-current="page"
      aria-disabled="true"
      className={cn('font-normal text-foreground', className)}
      role="link"
      {...otherProps}
    />
  );
};
BreadcrumbPage.displayName = 'BreadcrumbPage';

const BreadcrumbSeparator = (props: React.ComponentProps<'li'>) => {
  const { children, className, ...otherProps } = props;

  return (
    <li
      aria-hidden="true"
      className={cn('[&>svg]:w-3.5 [&>svg]:h-3.5', className)}
      role="presentation"
      {...otherProps}
    >
      {children ?? <ChevronRight />}
    </li>
  );
};
BreadcrumbSeparator.displayName = 'BreadcrumbSeparator';

const BreadcrumbEllipsis = (props: React.ComponentProps<'span'>) => {
  const { className, ...otherProps } = props;

  return (
    <span
      aria-hidden="true"
      className={cn('flex h-9 w-9 items-center justify-center', className)}
      role="presentation"
      {...otherProps}
    >
      <MoreHorizontal className="h-4 w-4" />
      <span className="sr-only">More</span>
    </span>
  );
};
BreadcrumbEllipsis.displayName = 'BreadcrumbElipssis';

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
};
